import { SpinnerIcon } from '@acceso/design-system';
import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { SwitchButton } from '@/components/SwitchButton/SwitchButton';
import { broadcastScheduleQueryKeys } from '@/features/broadcast-schedule/api/query-keys';
import { useGetBroadcastScheduleById } from '@/features/broadcast-schedule-program/api/get-broadcast-schedule-by-id';
import { useGetClipById } from '@/features/clips/api/get-clip-by-id';
import { MentionShowSelectedFilters } from '@/features/mentions/components/MentionFilters/components/MentionShowSelectedFilters/MentionShowSelectedFilters';
import { TSegment } from '@/features/segments/types';
import { useSearchParams } from '@/hooks/react-router/use-search-params';
import { usePresenceLease } from '@/hooks/usePresenceLease';
import { useUnderMinDesktopWidth } from '@/hooks/useUnderMinDesktopWidth';
import { useWebSocket } from '@/providers/websocket';
import { useProgramBroadcastFilters } from '@/stores/program-broadcast-filters';
import { useReport } from '@/stores/report-store';

import { useGetBroadcastScheduleSegmentById } from '../api/get-broadcast-schedule-by-id-segment';
import { BroadcastScheduleProgramBody } from '../components/BroadcastScheduleProgramBody/BroadcastScheduleProgramBody';
import { ESegmentToolkit } from '../components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/types';
import { BroadcastScheduleProgramHeaderButtonFilters } from '../components/BroadcastScheduleProgramHeaderButtonFilters/BroadcastScheduleProgramHeaderButtonFilters';
import { BroadcastScheduleProgramHeaderFilters } from '../components/BroadcastScheduleProgramHeaderFilters/BroadcastScheduleProgramHeaderFilters';
import { BroadcastScheduleProgramHeaderName } from '../components/BroadcastScheduleProgramHeaderName/BroadcastScheduleProgramHeaderName';
import { BroadcastScheduleProgramHeaderSearch } from '../components/BroadcastScheduleProgramHeaderSearch/BroadcastScheduleProgramHeaderSearch';
import { useReportDrawerController } from '../components/ReportDrawer/hooks/useReportDrawerControllers';
import { ReportDrawer } from '../components/ReportDrawer/ReportDrawer';
import { useBroadcastScheduleProgram } from '../context/BroadcastScheduleProgramContext';
import { findSegmentByDate } from '../helpers/find-segment-by-date';
import { useActiveOptionSegmentsEdits } from '../hooks/use-active-option-segments-edits';
import { useParrillaSegmentsTour } from '../hooks/use-parrilla-segments-tour';
import { useProgramBroadcastActiveIds } from '../hooks/use-program-broadcast-active-ids';
import { ProgramGlobalViewComponent } from '../hooks/useToolkitState';

// RISK: must stay one stable array. The global view copies `allSegments` into the context on
// every change, so a fresh `[]` per render while the list reloads re-renders in a loop.
const NO_SEGMENTS: TSegment[] = [];

export default function BroadcastScheduleProgramPage() {
  const {
    isHeaderVisible,
    videoControlRef,
    handleResetVisibleBroadcastProgramCards,
    setIsParrillaSegments,
    drawnFormReset,
    setActiveSegment,
    setSegmentItems,
    setVisibleBroadcastProgramCards,
    handleTooltikClick,
    activeSegment,
    setActiveClip,
    setFormBox,
    setCutData,
    pinnedId,
    setProgramNavigationState,
  } = useBroadcastScheduleProgram();
  const { id = '' } = useParams();
  const [searchParams] = useSearchParams();
  const clipIdParam = searchParams.get('clip_id') || '';
  const gteParam = searchParams.get('gte') || '';
  const lteParam = searchParams.get('lte') || '';
  const { data } = useGetBroadcastScheduleById({ id });
  const { connectProgram, disconnectProgram } = useWebSocket();
  const { report } = useReport();
  const { handleCreateReport, handleOnClose } = useReportDrawerController();
  const { startTour } = useParrillaSegmentsTour();
  const { underMinDesktopWith } = useUnderMinDesktopWidth();
  const [initialized, setInitialized] = useState<boolean>(false);
  const [handledClipId, setHandledClipId] = useState<string | null>(null);
  const [isGlobalViewMode, setIsGlobalViewMode] = useState(false);
  const { t } = useTranslation();

  usePresenceLease(id, connectProgram, disconnectProgram);

  const {
    review_states,
    time_keeping_states,
    gte,
    lte,
    search,
    workspaces,
    tags,
    logos,
    people,
    isStreaming,
    order_by,
    clearFilters,
  } = useProgramBroadcastFilters();
  const filters = {
    review_states,
    time_keeping_states,
    gte,
    lte,
    search,
    workspaces,
    tags,
    logos,
    people,
    isStreaming,
    order_by,
  };

  const queryKey = broadcastScheduleQueryKeys.id(id, filters);

  const { data: selectedClip = null } = useGetClipById({ id: clipIdParam });

  const {
    data: programSegmentsData = null,
    isLoading: isLoadingProgramSegments,
    isFetching: isFetchingProgramSegments,
  } = useGetBroadcastScheduleSegmentById({
    id,
    filters,
  });

  // RISK: set in the same effect that copies the data into `segmentItems`. Until both match,
  // the view on screen still shows the previous answer, and options read from it would too.
  const [copiedSegmentsData, setCopiedSegmentsData] = useState<TSegment[] | null>(null);
  const listIsSettled =
    !isFetchingProgramSegments &&
    programSegmentsData !== null &&
    copiedSegmentsData === programSegmentsData;

  useActiveOptionSegmentsEdits({ id, programSegmentsData, listIsSettled });

  const [broadcastIdsOpen, setBroadcastIdsOpen] = useState(false);
  const broadcastActiveOptions = useProgramBroadcastActiveIds({
    id,
    enabled: broadcastIdsOpen,
    listIsSettled,
  });

  useEffect(() => {
    if (!id) return;

    if (gteParam && lteParam) {
      setProgramNavigationState({
        id,
        gte: gteParam,
        lte: lteParam,
      });
      return;
    }

    if (data?.start_datetime && data?.end_datetime) {
      setProgramNavigationState({
        id,
        gte: data.start_datetime,
        lte: data.end_datetime,
      });
    }
  }, [data?.end_datetime, data?.start_datetime, gteParam, id, lteParam, setProgramNavigationState]);

  useEffect(() => {
    if (!programSegmentsData) return;
    setSegmentItems(programSegmentsData);
    setCopiedSegmentsData(programSegmentsData);

    if (initialized) {
      if (activeSegment) {
        if (programSegmentsData.find(segment => segment.id === activeSegment.id)) {
          return;
        }
      }
      setVisibleBroadcastProgramCards(prev => {
        return {
          ...prev,
          segments: true,
          segmentToolkit: false,
          segmentDetail: false,
        };
      });
      setActiveSegment(null);
    }

    if (programSegmentsData && programSegmentsData.length !== 0) {
      if (!clipIdParam) {
        setActiveSegment(programSegmentsData[0]);
      }
      setInitialized(true);
      setVisibleBroadcastProgramCards(prev => {
        return {
          ...prev,
          segmentToolkit: !underMinDesktopWith,
          segmentDetail: true,
        };
      });

      if (!clipIdParam) {
        handleTooltikClick(ESegmentToolkit.CORTES);
      }
    }
  }, [programSegmentsData, initialized]);

  useEffect(() => {
    setIsParrillaSegments(true);
    if (!id) return;
    return () => {
      clearFilters();
      setProgramNavigationState(null);
      handleResetVisibleBroadcastProgramCards();
      setIsParrillaSegments(false);
      drawnFormReset();
      setActiveSegment(null);
      setActiveClip(null);
    };
  }, [id]);

  useEffect(() => {
    if (!selectedClip) return;
    if (!programSegmentsData || programSegmentsData.length === 0) return;

    // Always sync activeClip with the latest server data so that background refetches
    // (stale-while-revalidate) propagate fresh clip_logos/clip_peoples to the TimeKeeping panel.
    setActiveClip(selectedClip);

    // One-time activation side-effects only on first encounter of this clip.
    if (handledClipId === selectedClip.id) return;

    setCutData(selectedClip);
    setFormBox('cut');
    if (pinnedId !== ESegmentToolkit.CORTES) {
      handleTooltikClick(ESegmentToolkit.CORTES);
    }
    setVisibleBroadcastProgramCards(prev => ({
      ...prev,
      segments: !underMinDesktopWith,
      segmentToolkit: !underMinDesktopWith,
      segmentDetail: true,
    }));

    // Preselect the first segment that belongs to the clip interval (overlap)
    const clipStart = new Date(selectedClip.start_datetime).getTime();
    const clipEnd = new Date(selectedClip.end_datetime || selectedClip.start_datetime).getTime();

    const overlapped = programSegmentsData.filter(seg => {
      const segStart = new Date(seg.start_datetime).getTime();
      const segEnd = segStart + seg.duration_in_seconds * 1000;
      return segEnd >= clipStart && segStart <= clipEnd;
    });

    const firstSeg =
      overlapped
        .slice()
        .sort(
          (a, b) => new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime()
        )[0] || findSegmentByDate(programSegmentsData, selectedClip.start_datetime);

    if (firstSeg) {
      setActiveSegment(firstSeg);
    }

    setHandledClipId(selectedClip.id);
  }, [
    selectedClip,
    programSegmentsData,
    handledClipId,
    setActiveClip,
    setCutData,
    setFormBox,
    pinnedId,
    handleTooltikClick,
    setVisibleBroadcastProgramCards,
    setActiveSegment,
  ]);

  useEffect(() => {
    const primeraVez = localStorage.getItem('segmentParrillaTourVisto');
    if (!primeraVez) {
      startTour();
      setTimeout(() => {
        if (videoControlRef.current) {
          videoControlRef.current?.stop();
        }
      }, 2000);
      localStorage.setItem('segmentParrillaTourVisto', 'true');
    }
  }, []);

  return (
    <>
      <div className="flex flex-col gap-2" id="parrilla-segments-page">
        <BroadcastScheduleProgramHeaderName item={data} />
        <div className="flex flex-col gap-1 lg:flex-row">
          {!isHeaderVisible && <MentionShowSelectedFilters />}
          {isHeaderVisible && <BroadcastScheduleProgramHeaderSearch />}
          {isHeaderVisible && (
            <BroadcastScheduleProgramHeaderFilters
              reviewState
              workspaces
              tags
              logo
              people
              broadcastActiveOptions={broadcastActiveOptions}
              onActiveOptionsOpenChange={setBroadcastIdsOpen}
            />
          )}
        </div>
        <div className="flex items-center gap-2">
          <SwitchButton
            checked={isGlobalViewMode}
            onChange={setIsGlobalViewMode}
            label={t('Vista global')}
          />
          <BroadcastScheduleProgramHeaderButtonFilters
            isLoading={isLoadingProgramSegments}
            queryKey={queryKey}
          />
        </div>
        {isGlobalViewMode ? (
          <Suspense fallback={<SpinnerIcon className="animate-spin" />}>
            <ProgramGlobalViewComponent
              programBroadcast={data}
              allSegments={programSegmentsData ?? NO_SEGMENTS}
              isLoading={isLoadingProgramSegments}
            />
          </Suspense>
        ) : (
          <BroadcastScheduleProgramBody
            programBroadcast={data}
            isLoading={isLoadingProgramSegments}
          />
        )}
      </div>
      <ReportDrawer isOpen={report} onClose={handleOnClose} onSubmit={handleCreateReport} />
    </>
  );
}
